import PropTypes from "prop-types";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import ChartLegend from "./ChartLegend";

const UserCities = ({ users, hideLegend = false }) => {
  // Process user data to extract cities with role breakdown
  const processCityData = () => {
    if (!users || !Array.isArray(users) || users.length === 0) {
      console.log("UserCities: No users data available");
      return { cityData: [], hasData: false };
    }

    console.log("UserCities: Processing", users.length, "users");

    // Count users by city and role
    const cityMap = {};

    users.forEach((user) => {
      const district = user?.personalInfo?.addressInfo?.district;
      const role = user?.role;

      // Skip if no district or no role
      if (!district || !role) return;

      // Normalize district name (trim)
      const normalizedDistrict = district.trim();
      if (!normalizedDistrict) return;

      // Initialize district entry if doesn't exist
      if (!cityMap[normalizedDistrict]) {
        cityMap[normalizedDistrict] = {
          total: 0,
          nurse: 0,
          patient: 0,
          physiotherapist: 0,
        };
      }

      // Increment counts
      cityMap[normalizedDistrict].total++;
      if (role === "nurse") {
        cityMap[normalizedDistrict].nurse++;
      } else if (role === "patient") {
        cityMap[normalizedDistrict].patient++;
      } else if (role === "physiotherapist") {
        cityMap[normalizedDistrict].physiotherapist++;
      }
    });

    // Convert to array and sort by total users (descending)
    const cityArray = Object.entries(cityMap)
      .map(([city, counts]) => ({
        city,
        ...counts,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10); // Top 10 cities only

    // Filter out cities with 0 users
    const filteredCities = cityArray.filter((city) => city.total > 0);

    console.log(
      "UserCities: Found",
      filteredCities.length,
      "districts with data"
    );
    console.log(
      "UserCities: Top districts:",
      filteredCities.slice(0, 5).map((c) => c.city)
    );

    return {
      cityData: filteredCities,
      hasData: filteredCities.length > 0,
    };
  };

  const { cityData, hasData } = processCityData();

  // Prepare data for Highcharts
  const categories = cityData.map((item) => item.city);
  const nurseData = cityData.map((item) => item.nurse);
  const patientData = cityData.map((item) => item.patient);
  const physioData = cityData.map((item) => item.physiotherapist);

  const options = {
    chart: {
      type: "bar",
      height: null, // Let container control height
      backgroundColor: "transparent",
      spacingTop: 5,
      spacingBottom: 5,
      spacingLeft: 10,
      spacingRight: 10,
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: categories,
      title: {
        text: null,
      },
      lineColor: "#D7E4EB",
      lineWidth: 1,
      labels: {
        style: {
          color: "#474747",
          fontSize: "11px",
          fontWeight: "500",
        },
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: "",
      },
      allowDecimals: false,
      gridLineColor: "#f0f0f0",
      gridLineDashStyle: "dot",
      labels: {
        style: {
          color: "#474747",
          fontSize: "10px",
        },
      },
    },
    legend: {
      enabled: false, // Disabled - using common legend component
    },
    plotOptions: {
      bar: {
        stacking: "normal",
        dataLabels: {
          enabled: true,
          formatter: function () {
            return this.y > 0 ? this.y : null; // Only show non-zero values
          },
          style: {
            fontSize: "9px",
            fontWeight: "600",
            color: "#ffffff",
            textOutline: "none",
          },
        },
        pointWidth: 18, // Reduced bar width for better proportions
      },
      series: {
        borderRadius: 2,
        pointPadding: 0.15,
        groupPadding: 0.2,
      },
    },
    series: [
      {
        name: "Nurses",
        data: nurseData,
        color: "#A4E472",
        stack: "total",
      },
      {
        name: "Patients",
        data: patientData,
        color: "#A3C8F4",
        stack: "total",
      },
      {
        name: "Physiotherapists",
        data: physioData,
        color: "#9DAEF4",
        stack: "total",
      },
    ],
    credits: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      backgroundColor: "#ffffff",
      borderColor: "#cccccc",
      borderRadius: 8,
      style: {
        fontSize: "11px",
      },
      formatter: function () {
        let tooltip = `<b>${this.x}</b><br/>`;
        let total = 0;
        this.points.forEach((point) => {
          if (point.y > 0) {
            tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${point.y}</b><br/>`;
            total += point.y;
          }
        });
        tooltip += `<b>Total: ${total}</b>`;
        return tooltip;
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              enabled: false,
            },
            yAxis: {
              labels: {
                style: {
                  fontSize: "9px",
                },
              },
            },
            xAxis: {
              labels: {
                style: {
                  fontSize: "9px",
                },
              },
            },
          },
        },
      ],
    },
  };

  const legendItems = [
    { label: "Nurse", color: "#A4E472" },
    { label: "Patient", color: "#A3C8F4" },
    { label: "Physiotherapist", color: "#9DAEF4" },
  ];

  return (
    <div
      className={`bg-white h-[35vh] shadow-md flex flex-col ${
        hideLegend ? "rounded-xl" : "rounded-xl"
      }`}
    >
      <div className="px-5 pt-4 pb-2 flex-shrink-0">
        <h4 className="text-base font-bold text-gray-800 p-1">
          Users By Cities
        </h4>
      </div>
      <div className="border-b border-gray-200 flex-shrink-0"></div>
      {!hideLegend && <ChartLegend items={legendItems} />}
      {hasData ? (
        <div className="px-4 py-3 flex-1 overflow-auto">
          <HighchartsReact highcharts={Highcharts} options={options} />
        </div>
      ) : (
        <div className="flex-1 flex justify-center items-center">
          <p className="text-gray-500 text-sm">
            No district data available for selected period
          </p>
        </div>
      )}
    </div>
  );
};

UserCities.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.string,
      personalInfo: PropTypes.shape({
        addressInfo: PropTypes.shape({
          district: PropTypes.string,
        }),
      }),
    })
  ),
  hideLegend: PropTypes.bool,
};

export default UserCities;
